<template>
  <div class="qc-report-print">
    <div class="print-header">
      <el-button type="primary" size="small" @click="handlePrint">打印</el-button>
    </div>

    <div class="print-content">
      <div class="print-content-inner" id="printArea">
        <!-- 标题 -->
        <h2 class="report-title">车辆运营安全与服务监控巡查日志</h2>

        <!-- 基本信息 -->
        <div class="info-section">
          <div class="info-row">
            <span class="info-label">巡查日期：</span>
            <span class="info-value">{{ date }}</span>
            <span class="info-label">巡查人员：</span>
            <span class="info-value">郑梦文</span>
          </div>
        </div>

        <!-- 巡查类目列表 -->
        <div class="category-section" v-for="(category, index) in categoryList" :key="index">
          <div class="category-header">
            <span class="category-title">{{ category.title }}</span>
          </div>
          <div class="category-content">
            <div class="category-desc">
              <span class="desc-label">类目描述：</span>
              <span class="desc-value">{{ category.checkItems }}</span>
            </div>
            <div class="category-info">
              <span class="info-item">
                <span class="item-label">巡查车辆/司机数量：</span>
                <span class="item-value">{{ category.checkCount }}</span>
              </span>
            </div>
            <div class="driver-list">
              <span class="list-label">巡查名单：</span>
              <div class="driver-nos">
                <span v-for="(driverNo, idx) in category.driverNoList" :key="idx" class="driver-no">
                  {{ driverNo }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 不合格司机汇总 -->
        <div class="summary-section">
          <div class="summary-title">不合格司机汇总</div>

          <div
            v-for="(category, index) in categoryList"
            :key="'summary-' + index"
            class="summary-table-wrapper"
          >
            <div class="summary-category-title">{{ category.summaryTitle }}</div>
            <table
              class="summary-table"
              v-if="category.detailList && category.detailList.length > 0"
            >
              <thead>
                <tr>
                  <th width="15%">司机编号</th>
                  <th width="40%">不合格事项</th>
                  <th width="15%">累计次数</th>
                  <th width="30%">整改举措</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(detail, idx) in category.detailList" :key="idx">
                  <td>{{ detail.driverNo }}</td>
                  <td>{{ detail.checkItem }}</td>
                  <td>{{ detail.totalTimes }}</td>
                  <td>{{ detail.result }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="empty-table">无不合格司机</div>
          </div>
        </div>

        <!-- 备注 -->
        <div class="remark-section">
          <div class="remark-label">巡查备注：</div>
          <div class="remark-content">{{ remarks || "无" }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDailyQcSummary } from "@/api";
import { DateUtil } from "@/util";

export default {
  name: "QcReportPrint",
  data() {
    return {
      date: "",
      fillTime: "",
      categoryList: [],
      remarks: ""
    };
  },
  created() {
    const date = this.$route.query.date;
    if (date) {
      this.date = date;
      this.loadData();
    } else {
      this.$message.error("缺少日期参数");
    }
  },
  methods: {
    loadData() {
      // 设置填写时间
      const now = new Date();
      this.fillTime = `${DateUtil.format(now, "yyyy-MM-dd 18:00")}`;

      // 加载数据
      getDailyQcSummary(this.date)
        .then((res) => {
          const dataList = res.data || [];
          this.processData(dataList);
        })
        .catch((err) => {
          console.error(err);
          this.$message.error("加载数据失败");
        });
    },
    processData(dataList) {
      // 收集所有备注
      const allRemarks = [];

      // 处理每个类目的数据
      this.categoryList = dataList.map((item) => {
        // 确定类目类型（通过检查 checkItems 来判断）
        const categoryType = item.type;

        // 处理司机编号列表
        const driverNos = item.driverNos || "";
        const driverNoList = driverNos ? driverNos.split(",").filter((no) => no.trim()) : [];

        // 收集备注
        if (item.remarks) {
          allRemarks.push(item.remarks);
        }

        return {
          title: categoryType + "巡查类目",
          summaryTitle: categoryType + "巡查不合格司机",
          checkItems: item.checkItems || "",
          checkCount: item.checkCount || 0,
          driverNoList: driverNoList,
          detailList: item.detailList || []
        };
      });

      // 按照固定顺序排序：安全硬件、安全行为、服务
      const orderMap = { 安全硬件: 1, 安全行为: 2, 服务: 3, 其他: 4 };
      this.categoryList.sort((a, b) => {
        const aType = this.getCategoryType(a.title);
        const bType = this.getCategoryType(b.title);
        return (orderMap[aType] || 99) - (orderMap[bType] || 99);
      });

      // 合并备注
      this.remarks = allRemarks.join("；") || "";
    },
    getCategoryType(title) {
      if (title.includes("安全硬件")) return "安全硬件";
      if (title.includes("安全行为")) return "安全行为";
      if (title.includes("服务")) return "服务";
      return "其他";
    },
    handlePrint() {
      window.print();
    }
  }
};
</script>

<style lang="less" scoped>
.qc-report-print {
  .print-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 20px;
    @media print {
      display: none;
    }
  }

  .print-content {
    display: flex;
    justify-content: center;
    padding: 20px;

    .print-content-inner {
      width: 210mm;
      min-height: 297mm;
      padding: 20mm;
      background: white;
      box-sizing: border-box;

      .report-title {
        text-align: center;
        font-size: 24px;
        font-weight: bold;
        margin: 0 0 20px 0;
        padding-bottom: 10px;
        border-bottom: 2px solid #000;
      }

      .report-note {
        margin-bottom: 20px;
        .note-text {
          font-size: 12px;
          color: #666;
        }
      }

      .info-section {
        margin-bottom: 30px;
        display: flex;
        justify-content: flex-end;
        .info-row {
          margin-bottom: 10px;
          font-size: 14px;
          .info-label {
            font-weight: bold;
          }
          .info-value {
            margin-right: 20px;
          }
        }
      }

      .category-section {
        margin-bottom: 30px;
        page-break-inside: avoid;

        .category-header {
          margin-bottom: 10px;
          .category-title {
            font-size: 16px;
            font-weight: bold;
          }
        }

        .category-content {
          padding-left: 20px;
          .category-desc {
            margin-bottom: 10px;
            font-size: 14px;
            .desc-label {
              font-weight: bold;
            }
          }

          .category-info {
            margin-bottom: 10px;
            font-size: 14px;
            .info-item {
              .item-label {
                font-weight: bold;
              }
            }
          }

          .driver-list {
            font-size: 14px;
            margin-top: 10px;
            .list-label {
              font-weight: bold;
              display: block;
              margin-bottom: 8px;
            }
            .driver-nos {
              display: flex;
              flex-wrap: wrap;
              gap: 6px;
              line-height: 1.8;
              .driver-no {
                display: inline-block;
                font-size: 13px;
                margin-right: 8px;
              }
            }
          }
        }
      }

      .summary-section {
        margin-top: 40px;
        .summary-title {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 20px;
          text-align: center;
        }

        .summary-table-wrapper {
          margin-bottom: 30px;
          page-break-inside: avoid;

          .summary-category-title {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 10px;
            padding-left: 10px;
            border-left: 4px solid #000;
          }

          .summary-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 13px;
            margin-bottom: 20px;
            border: 1px solid #000;

            thead {
              background: #f5f5f5;
              tr {
                th {
                  padding: 10px 8px;
                  border: 1px solid #000;
                  text-align: center;
                  font-weight: bold;
                  background: #e8e8e8;
                }
              }
            }

            tbody {
              tr {
                td {
                  padding: 8px;
                  border: 1px solid #000;
                  text-align: center;
                }
                &:nth-child(even) {
                  background: #fafafa;
                }
              }
            }
          }

          .empty-table {
            text-align: center;
            padding: 20px;
            color: #999;
            font-size: 14px;
          }
        }
      }

      .remark-section {
        margin-top: 40px;
        font-size: 14px;
        .remark-label {
          font-weight: bold;
          margin-bottom: 10px;
        }
        .remark-content {
          min-height: 60px;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
      }
    }
  }
}

@media print {
  .qc-report-print {
    .print-content {
      padding: 0;
      .print-content-inner {
        width: 100%;
        padding: 20mm;
        box-shadow: none;
      }
    }
  }

  @page {
    size: A4;
    margin: 0;
  }
}
</style>
